import { useV2ListTagsForAccountQuery } from "@generated";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import { Privileges } from "../../../types/user";
import SearchSelect, { SearchSelectOption } from "../SearchSelect";
import useInfiniteSelectOptions, {
  PagedQueryHook,
} from "../Select2/useInfiniteSelectOptions";
import { isNewTagName } from "./tagNames";

interface TagsFieldProps {
  id: string;
  label?: string;
  // Tag names.
  value: string[];
  onChange: (tags: string[]) => void;
  // A paged list hook for tags; defaults to the account's tags.
  useQuery?: PagedQueryHook;
  isDisabled?: boolean;
  placeholder?: string;
}

const toOption = (tag: { attributes: { name: string } }) => ({
  value: tag.attributes.name,
  label: tag.attributes.name,
});

// A created option carries the raw typed text: trim it, and drop repeats
// (ignoring case) so padding can't create a near-duplicate tag.
const toTagNames = (options: SearchSelectOption[]): string[] => {
  const seen = new Set<string>();
  return options
    .map((option) => option.label.trim())
    .filter((name) => {
      const key = name.toLowerCase();
      if (!name || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

/**
 * The tag picker for every form. Searches the account's tags as the user
 * types; users with "manage" on settings.tags may also create a new tag.
 */
const TagsField = ({
  id,
  label = "Tags",
  value,
  onChange,
  useQuery = useV2ListTagsForAccountQuery as PagedQueryHook,
  isDisabled,
  placeholder = "Search Tags",
}: TagsFieldProps): JSX.Element => {
  const { accessLevel } = usePrivilegeCheck();
  // Always creatable so the select isn't swapped (and remounted) when the
  // privilege loads; creation is gated by isValidNewOption instead.
  const canCreate = accessLevel(Privileges.SETTINGS_TAGS) === "manage";
  const { options, ...searchProps } = useInfiniteSelectOptions({
    useQuery,
    toOption,
  });

  return (
    <SearchSelect
      {...searchProps}
      isMulti
      // toOption always yields string values.
      options={options as SearchSelectOption[]}
      id={id}
      label={label}
      value={value.map((name) => ({ value: name, label: name }))}
      onChange={(selected) => onChange(toTagNames(selected))}
      isCreatable
      isValidNewOption={(input, selected, all) =>
        canCreate && isNewTagName(input, selected, all)
      }
      isDisabled={isDisabled}
      placeholder={placeholder}
    />
  );
};

export default TagsField;
